"use client"

import React, { useRef, useState } from "react"
import { Pressable, ScrollView } from "react-native"
import { FormattedDate, FormattedMessage, FormattedTime } from "react-intl"

import {
  B,
  Box,
  Button,
  H3,
  isWeb,
  P,
  ScrollBox,
  Sheet,
  Span,
  useExperienceAttendancePopupSeen,
  useRecordAttendance,
} from "@bullseye/components"
import type { Modal } from "@bullseye/types"

import { ApplicationModal, useApplicationModalContext } from "."

type ExperienceAttendance = {
  id: string
  title: string
  startDate: Date
  hostName: string
}

export type EventAttendanceModalProps = {
  experiences: ExperienceAttendance[]
}

export const EventAttendanceModal = ({
  isOpen,
  dismiss,
  experiences,
}: Modal<EventAttendanceModalProps>) => {
  const safeExperiences = experiences || []

  const ref = useRef<ScrollView>()

  // Track responses for each event
  const [responses, setResponses] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { recordAttendance } = useRecordAttendance()
  const { recordExperienceAttendancePopupSeen } =
    useExperienceAttendancePopupSeen()
  const [showSorry, setShowSorry] = useState(false)
  const [isScrollable, setIsScrollable] = useState(false)
  const scrollViewHeight = 640

  const handleResponse = (experienceId: string, attended: boolean) => {
    void recordPopupSeen()
    setResponses((prev) => ({
      ...prev,
      [experienceId]: attended,
    }))
  }
  const { openModal } = useApplicationModalContext()
  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const promises = safeExperiences.map(async (experience) => {
        if (responses[experience.id] === undefined) {
          return
        }
        const attended = responses[experience.id] ?? false
        return recordAttendance(experience.id, attended)
      })

      await Promise.all(promises)

      const shouldShowSorry = () => {
        return safeExperiences.every(
          (exp) =>
            responses[exp.id] === false || responses[exp.id] === undefined,
        )
      }

      if (shouldShowSorry()) {
        setShowSorry(true)
      } else {
        dismiss()
      }

      // The user is taken right to the post-experience survey.
      // Only show surveys for experiences where the user attended.
      const attendedExperiences = safeExperiences.filter(
        (exp) => responses[exp.id] === true,
      )
      if (attendedExperiences.length > 0) {
        openModal(ApplicationModal.PostExperienceSurvey, {
          experiences: attendedExperiences.map((exp) => ({
            experienceId: exp.id,
            title: exp.title,
            startDate: exp.startDate,
            hostName: exp.hostName,
          })),
        })
      }
    } catch (error) {
      console.error("Failed to submit attendance:", error)
      // TODO: Show error toast
    } finally {
      setIsSubmitting(false)
    }
  }

  const recordPopupSeen = async () => {
    const promises = safeExperiences.map(async (experience) => {
      return recordExperienceAttendancePopupSeen(experience.id)
    })

    await Promise.all(promises)
  }

  const handleDismiss = async () => {
    await recordPopupSeen()
    dismiss()
  }

  // Check if all questions are answered
  const anyAnswered = safeExperiences.some(
    (exp) => responses[exp.id] !== undefined,
  )

  let header
  if (safeExperiences.length > 1) {
    header = <FormattedMessage id="survey.attendance.headingPlural" />
  } else {
    header = <FormattedMessage id="survey.attendance.heading" />
  }

  const subheading = <FormattedMessage id="survey.attendance.subheading" />

  if (showSorry) {
    return (
      <Sheet
        show={isOpen}
        icon="x"
        onIconPress={() => dismiss()}
        dismiss={dismiss}
        disableSafeArea={isWeb()}
        renderContent={() => (
          <Box className="flex w-full flex-col items-center">
            <H3 className="mb-2 px-1 text-2xl font-semibold text-gray-900">
              <FormattedMessage id="survey.attendance.sorryHeading" />
            </H3>
            <P className="text-md mb-2 mt-2 !px-1 text-left !text-gray-900">
              <FormattedMessage id="survey.attendance.sorryText" />
            </P>
            <Button as="button" handlePress={dismiss} className="mt-4 w-full">
              <FormattedMessage id="survey.done" />
            </Button>
          </Box>
        )}
      />
    )
  }

  const headingAlignment = isWeb() ? "text-left" : "text-center"
  const headingClassName = `${headingAlignment} text-3xl font-semibold px-1 text-gray-900`

  return (
    <Sheet
      show={isOpen}
      icon="x"
      onIconPress={() => handleDismiss()}
      dismiss={handleDismiss}
      disableSafeArea={isWeb()}
      renderContent={() => {
        return (
          <Box className="flex items-center pb-1 g-1">
            <ScrollBox
              ref={ref}
              className="h-640 w-full"
              style={{ maxHeight: scrollViewHeight }}
              contentContainerStyle={[
                isScrollable && isWeb() ? { paddingRight: 16 } : null,
              ]}
              scrollEnabled
              onContentSizeChange={(_, contentHeight) => {
                setIsScrollable(contentHeight > scrollViewHeight)
              }}
              showsVerticalScrollIndicator={isWeb()}
            >
              <Pressable
                onPress={() => {
                  // No-op
                }}
                accessibilityRole="none"
              >
                <Box className="h-4 w-full" />
                <H3 className={headingClassName}>{header}</H3>
                <P className="text-md mb-2 mt-4 !px-1 text-left !text-gray-900">
                  {subheading}
                </P>
                {safeExperiences.map((experience) => (
                  <EventAttendanceQuestion
                    key={experience.id}
                    experience={experience}
                    response={responses[experience.id]}
                    onResponse={(attended) =>
                      handleResponse(experience.id, attended)
                    }
                  />
                ))}
                <Button
                  as="button"
                  handlePress={handleDismiss}
                  className="mt-4"
                  variant="secondary"
                >
                  <FormattedMessage id="survey.skipForNow" />
                </Button>
                {anyAnswered && (
                  <Button
                    as="button"
                    handlePress={handleSubmit}
                    disabled={!anyAnswered || isSubmitting}
                    className="mt-4"
                  >
                    {isSubmitting ? (
                      <FormattedMessage id="survey.submitting" />
                    ) : (
                      <FormattedMessage id="survey.submit" />
                    )}
                  </Button>
                )}
              </Pressable>
            </ScrollBox>
          </Box>
        )
      }}
    />
  )
}

// Individual question component
const EventAttendanceQuestion = ({
  experience,
  response,
  onResponse,
}: {
  experience: ExperienceAttendance
  response: boolean | undefined
  onResponse: (attended: boolean) => void
}) => {
  return (
    <Box className="w-full px-1 py-3">
      <Span className="mb-1 text-xl text-gray-900">
        <B>{experience.title}</B>
      </Span>
      <Span className="mb-2 text-base text-gray-900">
        <FormattedDate
          value={experience.startDate}
          month="long"
          year="numeric"
          day="numeric"
        />
        {" at "}
        <FormattedTime
          value={experience.startDate}
          hour="numeric"
          minute="numeric"
        />
      </Span>
      <Box className="flex flex-row">
        <Button
          as="button"
          outlined={response !== true}
          handlePress={() => onResponse(true)}
          compact
          className="-mr-1 items-center justify-center rounded-l-lg rounded-r-none text-center"
        >
          <FormattedMessage id="survey.attendance.yesAttended" />
        </Button>
        <Button
          as="button"
          outlined={response !== false}
          handlePress={() => onResponse(false)}
          compact
          className="-ml-1 items-center justify-center rounded-l-none rounded-r-lg text-center"
        >
          <FormattedMessage id="survey.attendance.noDidNotAttend" />
        </Button>
      </Box>
    </Box>
  )
}
